import React from "react";
import { Validators, Fields } from "./TinyImporter.types";
import { CustomAttributeDefinitionView } from "@tiny/api";

export default function (
  customAttributes: CustomAttributeDefinitionView[] = [],
  smsEnabled = false
) {
  const emailValidators: Validators = [
    { validate: "required" },
    { validate: "unique" },
    {
      validate: "regex_matches",
      /*eslint quotes: [2, "double", "avoid-escape"]*/
      regex:
        "^[-!#$%&'*+/0-9=?A-Z^_a-z{|}~](\\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\\.?[a-zA-Z0-9])*\\.[a-zA-Z](-?[a-zA-Z0-9])+$",
      error:
        "Please provide a valid email address without any leading or trailing spaces. This field is required and must be unique.",
    },
  ];
  const nameValidators: Validators = [
    {
      validate: "regex_matches",
      regex: "^.{0,64}$",
      error: "The maximum number of characters is 64",
    },
  ];
  const statusValidators: Validators = [
    {
      validate: "regex_matches",
      regex: "^$|^(Subscribed|Unsubscribed|Bounced|Inactive|Spam)$",
      error:
        "The status should be 'Subscribed', 'Unsubscribed', 'Bounced', 'Inactive' or 'Spam'.Please ensure correct capitalization and no extra leading or trailing spaces.",
    },
  ];
  const dateValidators: Validators = [
    {
      validate: "regex_matches",
      regex:
        "^((0?[1-9]|1[012])[-/.](0?[1-9]|[12][0-9]|3[01])[-/.](19|20)[0-9]{2})*$",
      error: "Invalid Date Format,The expected format is MM/dd/yyyy",
    },
  ];
  const decimalNumberValidator: Validators = [
    {
      validate: "regex_matches",
      regex: "^([1-9]\\d*|0)(\\.\\d+)?$",
      error: "The value must be a decimal number",
    },
  ];
  const numberValidators: Validators = [
    {
      validate: "regex_matches",
      // Matches empty string or a whole number between 0 and 2147483647
      regex:
        "^$|^(?:0|[1-9]\\d{0,8}|1\\d{9}|20\\d{8}|21[0-3]\\d{7}|214[0-6]\\d{6}|2147[0-3]\\d{5}|21474[0-7]\\d{4}|214748[0-2]\\d{3}|2147483[0-5]\\d{2}|21474836[0-3]\\d|214748364[0-7])$",
      error: "The value must be a whole number between 0 and 2147483647",
    },
  ];
  const addressValidators: Validators = [
    {
      validate: "regex_matches",
      regex: "^.{0,128}$",
      error: "The maximum number of characters is 128",
    },
  ];
  const otherValidators: Validators = [
    {
      validate: "regex_matches",
      regex: "^.{0,64}$",
      error: "The maximum number of characters is 64",
    },
  ];
  // SMS sends to E.164 numbers only; other accounts keep free-form phones.
  const phoneValidators: Validators = smsEnabled
    ? [
        {
          validate: "regex_matches",
          // Optional, but when present must be E.164: +, country code, up to 15 digits.
          regex: "^$|^\\+[1-9]\\d{1,14}$",
          error:
            "Phone number must be in E.164 format: + followed by the country code and number, with no spaces or dashes (e.g. +14155552671).",
        },
      ]
    : otherValidators;
  const fields: Fields = [
    { label: "Email", key: "email", validators: emailValidators },
    {
      label: "Phone number",
      key: "phone",
      validators: phoneValidators,
      description: smsEnabled ? "E.164 format, e.g. +14155552671" : undefined,
    },
    { label: "First Name", key: "firstName", validators: nameValidators },
    { label: "Last Name", key: "lastName", validators: nameValidators },
    { label: "Source", key: "source", validators: otherValidators },
    { label: "Status", key: "status", validators: statusValidators },
    {
      label: "Birthday",
      key: "birthday",
      validators: dateValidators,
      description: "Add date in the format MM/dd/yyyy",
    },
    {
      label: "Tags",
      key: "tags",
      description: "Add tags separated by pipes (|)",
    },
    { label: "Currency", key: "currency", validators: otherValidators },
    { label: "Country", key: "country", validators: otherValidators },
    { label: "State/Province", key: "province", validators: otherValidators },
    { label: "City", key: "city", validators: otherValidators },
    { label: "Address 1", key: "address1", validators: addressValidators },
    { label: "Address 2", key: "address2", validators: addressValidators },
    {
      label: "Zip/Postal Code",
      key: "postalCode",
      validators: otherValidators,
    },
    { label: "Company", key: "company", validators: otherValidators },
    {
      label: "Number of orders",
      key: "ordersCount",
      validators: numberValidators,
    },
    {
      label: "Lifetime Value",
      key: "totalSpent",
      validators: decimalNumberValidator,
    },
    {
      label: "Latest order ID",
      key: "lastOrderId",
      validators: otherValidators,
    },
    {
      label: "Last purchased order",
      key: "lastOrderName",
      validators: otherValidators,
    },
    {
      label: "Last purchased date",
      key: "lastOrderDate",
      validators: dateValidators,
      description: "Add date in the format MM/dd/yyyy",
    },
    {
      label: "Last purchased price",
      key: "lastOrderPrice",
      validators: decimalNumberValidator,
    },
  ];

  const booleanValidators: Validators = [
    {
      validate: "regex_matches",
      regex: "^$|^(true|false|TRUE|FALSE)$",
      error: "The value must be true or false",
    },
  ];

  const customAttributeFields: Fields = customAttributes.map((attr) => {
    let validators: Validators | undefined;
    let description: string | undefined;

    switch (attr.dataType) {
      case "INTEGER":
        validators = numberValidators;
        break;
      case "BOOLEAN":
        validators = booleanValidators;
        break;
      case "DATE":
        validators = dateValidators;
        description = "Add date in the format MM/dd/yyyy";
        break;
      case "ARRAY":
        validators = undefined;
        description = "Add values separated by commas (,)";
        break;
      case "STRING":
      default:
        validators = otherValidators;
        break;
    }

    return {
      label: attr.attributeName,
      key: `customAttribute_${attr.attributeName}`,
      validators,
      description,
    };
  });

  const statusIndex = fields.findIndex((f) => f.key === "status");
  const insertAt = statusIndex >= 0 ? statusIndex + 1 : fields.length;

  return {
    fields: [
      ...fields.slice(0, insertAt),
      ...customAttributeFields,
      ...fields.slice(insertAt),
    ],
  };
}
